import { Directive, ElementRef, EventEmitter, HostListener, Input, OnInit, Output, inject } from '@angular/core';

/**
 * This directive detects when the user scrolled to the end of the page, respecting the threshold
 *
 * usage: <my-component appScrollNearEnd (nearEnd)="myMethod()"></my-component>
 * myMethod() will be called each time the current window is scrolled to the end of the page
 *
 * see https://dev.to/krivanek06/angular-infinite-scrolling-2jab
 */
@Directive( {
    selector: '[appScrollNearEnd]',
    standalone: false
} )
export class HisScrollNearEndDirective implements OnInit {
    private el = inject(ElementRef);

    @Output() nearEnd: EventEmitter<void> = new EventEmitter<void>();

    /**
     * threshold in PX when to emit before page end scroll
     */
    @Input() threshold = 120;

    @Input() minEmitDistance = 100;

    private window!: Window;

    private lastEmitPosition: number = 0;

    ngOnInit(): void {
        this.window = window;
    }

    @HostListener('window:scroll')
    windowScrollEvent() {
        // height of whole window page
        const heightOfWholePage = this.window.document.documentElement.scrollHeight;

        // how big in pixels the element is
        const heightOfElement = this.el.nativeElement.scrollHeight;

        // currently scrolled Y position
        const currentScrolledY = this.window.scrollY;

        // height of opened window - shrinks if console is opened
        const innerHeight = this.window.innerHeight;

        /**
         * the area between the start of the page and when this element is visible
         * in the parent component
         */
        const spaceOfElementAndPage = heightOfWholePage - heightOfElement;

        // calculated whether we are near the end
        const scrollToBottom =
            heightOfElement - innerHeight - currentScrolledY + spaceOfElementAndPage;

        //distance-based approach: only emit if we have moved from the previous position
        const distanceToLastEmit: number = Math.abs( currentScrolledY - this.lastEmitPosition );

        // if the user is near end
        if ( this.endPositionReached( scrollToBottom ) && this.hasMovedSignificantly( distanceToLastEmit ) ) {
            this.nearEnd.emit();
            this.lastEmitPosition = currentScrolledY;
        }
    }

    private hasMovedSignificantly( distanceToLastEmit: number ): boolean {
        return distanceToLastEmit > this.minEmitDistance;
    }

    private endPositionReached( scrollToBottom: number ): boolean {
        return scrollToBottom < this.threshold;
    }
}
